import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useCreateProductReview } from '@monofront/business/hooks';
import Button from '@monofront/design-system/components/Button';
import Label from '@monofront/design-system/components/Form/Label';
import Textarea from '@monofront/design-system/components/Form/Textarea';
import { useAuthentication } from '@context/businessContainer';
import ReviewStarsSelect from '@components/ReviewStarsSelect';
import {
  IPrismicRichText,
  IProduct,
  ITrackReviewProductEvent,
} from '@monofront/types';
import { RichText } from 'prismic-reactjs';
import CustomPrismicLink from '@components/CustomPrismicLink';
import { dataLayerTrackEvent } from '@utils/tracking';
import { trackEvent } from '@monofront/tracking';
import { useTranslations } from 'next-intl';
import { getProductCategory } from '@monofront/business/utils';

interface ICreateReviewFormProps {
  productSku: string;
  createReviewParagraph: IPrismicRichText | null;
  initialRatingSelected?: null | number;
  onSuccess?: () => void;
  product: IProduct | null;
}

const CreateReviewForm: React.FC<ICreateReviewFormProps> = ({
  productSku,
  createReviewParagraph,
  initialRatingSelected = null,
  onSuccess,
  product,
}) => {
  const { error, called, isLoading, createProductReview } =
    useCreateProductReview();
  const { customer } = useAuthentication();
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues,
  } = useForm({
    mode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      rating: initialRatingSelected,
      comment: undefined as string | undefined,
    },
  });

  const tReviews = useTranslations('Reviews');
  const tGlobal = useTranslations('Global');
  const tErrors = useTranslations('Errors');

  useEffect(() => {
    if (!isLoading && called && error) {
      toast(tErrors('addReviewError'), {
        progressClassName: 'toast-error',
      });
    } else if (!isLoading && called && !error) {
      toast(tReviews('addReviewThanks'));
      setValue('rating', null);

      // deprecated tracker https://gitlab.com/la-fourche/scrum/-/issues/1968
      dataLayerTrackEvent('productReviewed');

      let eventData: ITrackReviewProductEvent['data'];

      if (product) {
        eventData = {
          products: [
            {
              name: product.title,
              sku: product.sku,
              price: product.price,
              brand: product.vendor,
              category: getProductCategory(product) ?? undefined,
            },
          ],
        };
      }

      trackEvent({ type: 'LFReviewProduct', data: eventData });
      reset();
      onSuccess && onSuccess();
    }
  }, [called, error, isLoading]);

  const triggerOnSubmit = ({ comment, rating }: Record<string, any>) => {
    createProductReview({
      sku: productSku,
      email: customer!.email,
      rating: rating + 1,
      comment,
    });
  };

  // Register custom component: https://github.com/react-hook-form/react-hook-form/issues/187#issuecomment-518616009
  useEffect(() => {
    register('rating', {
      required: {
        value: true,
        message: tReviews('chooseAReviewGrade'),
      },
    });
  }, [register]);

  return (
    <div className="container">
      <form onSubmit={handleSubmit(triggerOnSubmit)}>
        <div className="global-rating">
          <Label id="rating">{tReviews('globalGrade')}</Label>
          <ReviewStarsSelect
            ratingSelected={getValues().rating}
            setRatingSelected={(rating) => {
              setValue('rating', rating, {
                shouldValidate: true,
                shouldTouch: true,
                shouldDirty: true,
              });
            }}
          />
          {errors.rating?.message && (
            <p className="error tw-lf-error-message">
              {errors?.rating?.message as string}
            </p>
          )}
        </div>
        <div className="comment-input-block">
          <Label id="comment">
            {tReviews('yourComment')}{' '}
            <span className="max-length">
              ({tGlobal('nbChars', { nb: 500 })})
            </span>
          </Label>
          <Controller
            name="comment"
            control={control}
            rules={{
              required: {
                value: true,
                message: tReviews('requiredComment'),
              },
              maxLength: {
                value: 500,
                message: `${tGlobal('nbChars', { nb: 500 })} ${tGlobal(
                  'maximum',
                )}`,
              },
            }}
            render={({ field }) => {
              return (
                <Textarea
                  id="comment"
                  maxLength={500}
                  counterLimit={500}
                  {...field}
                />
              );
            }}
          />
          {errors.comment && (
            <p className="error tw-lf-error-message">
              {errors?.comment?.message as string}
            </p>
          )}
        </div>
        <div className="cta">
          <Button
            type="submit"
            isLoading={isLoading}
            block={false}
            dataTestId="CreateReviewForm-Button-2"
          >
            {tReviews('iReview')}
          </Button>
        </div>
      </form>
      {createReviewParagraph ? (
        <div className="paragraph">
          <RichText
            render={createReviewParagraph}
            serializeHyperlink={CustomPrismicLink}
          />
        </div>
      ) : null}
      <style jsx>{`
        .container {
          display: grid;
          grid-template-columns: 60% 40%;
          grid-row-gap: var(--space-6);
          margin-bottom: var(--space-6);
        }

        form :global(label) {
          font-weight: var(--font-weight-bold);
          margin-bottom: var(--space-2);
        }

        .comment-input-block,
        .global-rating,
        .paragraph {
          margin-bottom: var(--space-4);
        }

        .max-length {
          font-weight: var(--font-weight-light);
        }

        form {
          margin-right: var(--space-9);
        }

        form :global(#comment) {
          height: 150px;
        }

        form :global(.error) {
          margin-top: var(--space-2);
        }

        .paragraph :global(p) {
          margin-bottom: var(--space-5);
        }

        .paragraph :global(a) {
          color: var(--clr-tertiary-1);
          text-decoration: underline;
        }

        .paragraph :global(li) {
          list-style-type: disc;
          list-style-position: inside;
          margin-left: var(--space-3);
        }

        .cta :global(button) {
          min-width: 200px;
        }

        @media (max-width: 800px) {
          .container {
            display: inherit;
          }

          form {
            margin-right: inherit;
          }

          .paragraph {
            margin-top: var(--space-6);
          }
        }
      `}</style>
    </div>
  );
};

export default CreateReviewForm;
